import React, { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Mark, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';

/**
 * Custom Tiptap mark that preserves <span data-glossary-id="…"> through the
 * editor's HTML parse/serialise round-trip. Without this, Tiptap strips unknown
 * attributes and the glossary reference is lost.
 */
const GlossaryMark = Mark.create({
  name: 'glossaryTerm',
  spanning: true,
  addAttributes() {
    return {
      'data-glossary-id': {
        default: null,
        parseHTML: (el) => el.getAttribute('data-glossary-id'),
        renderHTML: (attrs) => ({ 'data-glossary-id': attrs['data-glossary-id'] }),
      },
    };
  },
  parseHTML() {
    return [{ tag: 'span[data-glossary-id]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['span', mergeAttributes(HTMLAttributes), 0];
  },
});

interface RichTextEditorProps {
  content: string;
  onChange: (html: string) => void;
  placeholder?: string;
  resetKey?: string;
  /** Optional extra buttons rendered at the right end of the toolbar. Receives the tiptap editor instance. */
  extraToolbar?: (editor: any) => React.ReactNode;
}

const MenuBar: React.FC<{ editor: any; extraToolbar?: (editor: any) => React.ReactNode }> = ({ editor, extraToolbar }) => {
  if (!editor) return null;

  const btnClass = (active: boolean) =>
    `px-2 py-1 text-sm rounded ${active ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} transition`;

  return (
    <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50 rounded-t-lg">
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={btnClass(editor.isActive('bold'))}>
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={btnClass(editor.isActive('italic'))}>
        <em>I</em>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={btnClass(editor.isActive('underline'))}>
        <u>U</u>
      </button>
      <span className="w-px bg-gray-300 mx-1" />
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} className={btnClass(editor.isActive('heading', { level: 2 }))}>
        H2
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} className={btnClass(editor.isActive('heading', { level: 3 }))}>
        H3
      </button>
      <span className="w-px bg-gray-300 mx-1" />
      <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} className={btnClass(editor.isActive('bulletList'))}>
        List
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} className={btnClass(editor.isActive('orderedList'))}>
        1. List
      </button>
      <span className="w-px bg-gray-300 mx-1" />
      <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} className={btnClass(editor.isActive('blockquote'))}>
        Quote
      </button>
      <button type="button" onClick={() => {
        const url = window.prompt('URL:');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      }} className={btnClass(editor.isActive('link'))}>
        Link
      </button>
      <button type="button" onClick={() => {
        const url = window.prompt('Image URL:');
        if (url) editor.chain().focus().setImage({ src: url }).run();
      }} className={btnClass(false)}>
        Img
      </button>
      {extraToolbar && (
        <>
          <span className="w-px bg-gray-300 mx-1" />
          {extraToolbar(editor)}
        </>
      )}
    </div>
  );
};

const RichTextEditor: React.FC<RichTextEditorProps> = ({ content, onChange, placeholder, resetKey, extraToolbar }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Image,
      GlossaryMark,
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  useEffect(() => {
    if (editor && resetKey !== undefined) {
      // emitUpdate:false avoids triggering onChange when resetting content externally
      editor.commands.setContent(content || '', { emitUpdate: false } as any);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
      <MenuBar editor={editor} extraToolbar={extraToolbar} />
      <EditorContent
        editor={editor}
        className="prose max-w-none p-4 min-h-[200px] focus:outline-none"
      />
    </div>
  );
};

export default RichTextEditor;
